import { FC, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';

import { useForm } from '@tanstack/react-form';

import {
  useToaster,
  ButtonText,
  SubmitButtonWrapper,
  TextBlock,
  Button,
  InputPassword,
  FormField,
  Title,
  Expander,
} from '../../components';
import {
  createValidator,
  OnboardChangePasswordDto,
} from '@skillarea/lib-shared';

import { TChangePasswordTexts } from '../../types';

import styles from './Widgets.module.scss';
import { EOnboardRoutes } from '../index';

const changePasswordValidator = createValidator(OnboardChangePasswordDto);

export const ChangePassword: FC<{
  texts: TChangePasswordTexts;
  onboardStore: any;
}> = ({ texts, onboardStore }) => {
  const { link } = useParams({ from: '/change/$link' });
  const navigate = useNavigate();
  const { toast } = useToaster();

  const [isLinkNotFound, setIsLinkNotFound] = useState(false);

  const navigateToLogin = () => {
    if (typeof window !== 'undefined') {
      window.location.replace('/#login');
      return;
    }

    void navigate({ to: '/', hash: EOnboardRoutes.login, replace: true });
  };

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      const result = await onboardStore.getState().changePassword({
        link: link || '',
        body: value,
      });
      if (result.success) {
        toast.success(texts.success);
        navigateToLogin();
      } else {
        // Handle errors for tanstack form
        console.error('Form submission error:', result);
      }
    },
    validators: {
      onChange: changePasswordValidator,
      onSubmit: changePasswordValidator,
    },
  });

  useEffect(() => {
    (async () => {
      if (!link) {
        void navigate({ to: '/', hash: EOnboardRoutes.login, replace: true });
      } else {
        const result = await onboardStore.getState().checkChangePasswordLink({
          link: link || '',
        });
        if (!result.success) setIsLinkNotFound(true);
      }
    })();
  }, [link, navigate, onboardStore]);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <Title className={styles.header} color="accent" size="xs">
        {texts.title}
      </Title>

      <TextBlock
        size="s"
        color="noteContrast"
        className={styles.text}
        line="150"
      >
        {texts.subTitle}
      </TextBlock>

      <FormField name="newPassword" form={form}>
        <InputPassword
          className={styles.topInput}
          disabled={isLinkNotFound}
          placeholder={texts.new}
        />
      </FormField>

      <FormField name="confirmPassword" form={form}>
        <InputPassword disabled={isLinkNotFound} placeholder={texts.confirm} />
      </FormField>

      <SubmitButtonWrapper
        forceDisabled={isLinkNotFound ? true : undefined}
        form={form}
      >
        <Button
          className={styles.noTextSubmit}
          type="submit"
          variant="filled"
          color="primary"
        >
          {texts.submit}
        </Button>
      </SubmitButtonWrapper>

      <div className={styles.footer}>
        <Expander />

        {isLinkNotFound && (
          <div className={styles.bottomSingleLink}>
            <Link to="/" hash={EOnboardRoutes.login}>
              <ButtonText size="xs" color="noteContrast">
                {texts.back}
              </ButtonText>
            </Link>
          </div>
        )}
      </div>
    </form>
  );
};
